import { LbNotificationExpiry } from '@electron/shared/lb-notification.types';
import {
  type MeetingInterruptReason,
  type MeetingUnexpectedStopCause,
} from '@electron/shared/meetings.types';
import { observable, runInAction } from 'mobx';
import { v4 as uuidv4 } from 'uuid';

import { logError } from '@/lib/errorLogging';

import { electronApis } from '../electron-api';

const unexpectedStopMessages = observable.map<string, string>();

const RECORDING_MEETING_KEY = 'meetings.recordingMeetingId';

export function rememberRecordingMeeting(meetingId: string | null) {
  if (meetingId) {
    sessionStorage.setItem(RECORDING_MEETING_KEY, meetingId.toLowerCase());
  } else {
    sessionStorage.removeItem(RECORDING_MEETING_KEY);
  }
}

export function wasRecordingBeforeReload(meetingId: string) {
  return (
    sessionStorage.getItem(RECORDING_MEETING_KEY) === meetingId.toLowerCase()
  );
}

export function getUnexpectedStopMessage(meetingId: string) {
  return unexpectedStopMessages.get(meetingId);
}

export function clearUnexpectedStop(meetingId: string) {
  runInAction(() => unexpectedStopMessages.delete(meetingId));
}

/** For recordings that end without the user's Stop or an intended auto-stop:
 * brings the main window to the front on the meeting, where a banner says why
 * the recording stopped, and sends a notification with the warning sound. */
export function alertUnexpectedRecordingStop({
  meetingId,
  cause,
  interruptReason,
  title,
  message,
}: {
  meetingId: string | null | undefined;
  cause: MeetingUnexpectedStopCause;
  interruptReason?: MeetingInterruptReason;
  title: string;
  message: string;
}) {
  electronApis.meetings.logRecordingHealthEvent({
    event_type: 'recording_unexpected_stop',
    meeting_id: meetingId ?? null,
    cause,
    interrupt_reason: interruptReason ?? null,
    app_focused: document.hasFocus(),
  });

  electronApis.lbNotifications
    .sendNotification({
      id: uuidv4(),
      view: 'basic',
      sound: 'problem',
      expiresAt: LbNotificationExpiry.alert(),
      payload: {
        title,
        message,
        ...(meetingId && {
          route: {
            to: '/meetings/$meetingId',
            params: { meetingId },
          },
        }),
      },
    })
    .catch((error) =>
      logError(
        error,
        '[meetings] Failed to send recording stopped notification'
      )
    );

  if (meetingId) {
    runInAction(() => unexpectedStopMessages.set(meetingId, message));
    electronApis.window
      .mainWindowNavigateTo('/meetings/$meetingId', {
        params: { meetingId },
        search: { tab: 'transcript' },
      })
      .catch((error) =>
        logError(error, '[meetings] Failed to open the stopped meeting')
      );
  }
  if (!document.hasFocus()) dropFocusWhenWindowActivates();
  electronApis.window
    .bringMainWindowToFront()
    .catch((error) =>
      logError(error, '[meetings] Failed to bring the main window to the front')
    );
}

const ACTIVATION_FOCUS_WINDOW_MS = 500;
const ACTIVATION_TIMEOUT_MS = 5_000;

/** The app takes focus without a user action, so whatever gets keyboard focus
 * receives keys meant for another app. A closed window reopens with focus on
 * the titlebar Back button, where a space switches to the autofocused notes
 * editor. Blurs focus until the user clicks or the window settles. */
function dropFocusWhenWindowActivates() {
  const blurActiveElement = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };
  const release = () => {
    clearTimeout(timeoutId);
    window.removeEventListener('focus', onWindowFocus);
    document.removeEventListener('focusin', blurActiveElement, true);
    document.removeEventListener('pointerdown', release, true);
  };
  const onWindowFocus = () => {
    window.removeEventListener('focus', onWindowFocus);
    clearTimeout(timeoutId);
    blurActiveElement();
    document.addEventListener('focusin', blurActiveElement, true);
    timeoutId = setTimeout(release, ACTIVATION_FOCUS_WINDOW_MS);
  };
  let timeoutId = setTimeout(release, ACTIVATION_TIMEOUT_MS);
  window.addEventListener('focus', onWindowFocus);
  document.addEventListener('pointerdown', release, true);
}
